import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

const Card = ({ children }) => (
    <div className="rounded-2xl shadow p-4 bg-white border">{children}</div>
);

const Pill = ({ active, onClick, children, accent }) => (
    <button
        onClick={onClick}
        className={`px-3 py-1 rounded-full border ${active ? "text-white" : "bg-white text-gray-700"}`}
        style={active ? { backgroundColor: accent, borderColor: accent } : undefined}
    >
        {children}
    </button>
);

const authHeaders = () => {
    const t = localStorage.getItem("token");
    return t ? { Authorization: `Bearer ${t}` } : {};
};

const Search = ({ value, onChange, placeholder, className = "" }) => (
    <input
        className={`border rounded px-3 py-1 w-full ${className}`}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
    />
);

const Modal = ({ open, onClose, children, title, footer, actions, accent }) => {
    if (!open) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden="true" />
            <div
                role="dialog"
                aria-modal="true"
                className="relative z-10 bg-white rounded-2xl shadow-xl w-[95vw] max-w-5xl max-h-[85vh] flex flex-col"
                style={{
                    "--module-accent": accent,
                    "--threedi-blue": "#25408f",
                }}
            >
                <div className="px-5 py-3 border-b flex items-center gap-3">
                    <div className="text-lg font-semibold truncate">{title}</div>
                    <div className="ml-auto flex items-center gap-2">{actions}</div>
                    <button className="px-3 py-1 border rounded" onClick={onClose} aria-label="Close">
                        Close
                    </button>
                </div>
                <div className="p-4 overflow-auto">{children}</div>
                {footer ? <div className="px-4 py-3 border-t">{footer}</div> : null}
            </div>
        </div>
    );
};

export default function PreferencesAlertsPanel({ apiBase = "", mode = "profiler" }) {
    const resolvedMode = String(mode || "").trim().toLowerCase() === "altdata" ? "altdata" : "profiler";
    const accent = resolvedMode === "altdata" ? "var(--altdata-red, #990026)" : "#E87722";
    const moduleLabel = resolvedMode === "altdata" ? "Alt Data" : "Profiler";
    const itemLabel = resolvedMode === "altdata" ? "Alt Data" : "";
    const withMode = (path) => `${apiBase}${path}${path.includes("?") ? "&" : "?"}mode=${resolvedMode}`;
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const [vendors, setVendors] = useState([]);
    const [products, setProducts] = useState([]);
    const [productTypes, setProductTypes] = useState([]);
    const [groups, setGroups] = useState([]);

    const [modalOpen, setModalOpen] = useState(false);
    const [modalTab, setModalTab] = useState("overview");
    const [selectedGroupId, setSelectedGroupId] = useState(null);

    const [vendorQ, setVendorQ] = useState("");
    const [productQ, setProductQ] = useState("");
    const [ptypeQ, setPtypeQ] = useState("");

    const [selectedListId, setSelectedListId] = useState(null);

    const selectedGroup = useMemo(
        () => groups.find((g) => g.id === selectedGroupId) || null,
        [groups, selectedGroupId]
    );

    const vendorById = useMemo(() => {
        const m = new Map();
        vendors.forEach((v) => m.set(v.id, v.name));
        return m;
    }, [vendors]);

    const productById = useMemo(() => {
        const m = new Map();
        products.forEach((p) => m.set(p.id, p.displayName));
        return m;
    }, [products]);

    const ptypeById = useMemo(() => {
        const m = new Map();
        productTypes.forEach((t) => m.set(t.id, t.name));
        return m;
    }, [productTypes]);

    useEffect(() => {
        let cancel = false;
        (async () => {
            try {
                setLoading(true);
                const { data } = await axios.get(
                    withMode(`/preferences-alerts/bootstrap`),
                    { headers: authHeaders() }
                );
                if (cancel) return;
                setVendors(data.vendors || []);
                setProducts(data.products || []);
                setProductTypes(data.producttypes || []);
                setGroups(data.groups || []);
                if ((data.groups || []).length) {
                    setSelectedGroupId(data.groups[0].id);
                    setSelectedListId(data.groups[0].id);
                }
            } catch (e) {
                console.error(e);
            } finally {
                if (!cancel) setLoading(false);
            }
        })();
        return () => {
            cancel = true;
        };
    }, [apiBase, resolvedMode]);

    const openGroupModal = (groupId) => {
        setSelectedGroupId(groupId);
        setModalTab("overview");
        setModalOpen(true);
    };

    const mutateGroup = (updater) => {
        setGroups((prev) => prev.map((g) => (g.id === selectedGroupId ? updater({ ...g }) : g)));
    };

    const toggleFlag = (key) => {
        if (!selectedGroup) return;
        mutateGroup((g) => {
            g.flags = { ...g.flags, [key]: !g.flags?.[key] };
            return g;
        });
    };

    const toggleId = (listKey, id, checked) => {
        if (!selectedGroup) return;
        mutateGroup((g) => {
            const set = new Set(g[listKey] || []);
            if (checked) set.add(id);
            else set.delete(id);
            g[listKey] = Array.from(set);
            return g;
        });
    };

    const setAllNone = (listKey, ids, all) => {
        if (!selectedGroup) return;
        mutateGroup((g) => {
            g[listKey] = all ? ids.slice() : [];
            return g;
        });
    };

    const saveCurrent = async () => {
        if (!selectedGroup) return;
        setSaving(true);
        try {
            await axios.put(
                withMode(`/preferences-alerts/groups/${selectedGroup.id}`),
                {
                    name: selectedGroup.name,
                    flags: selectedGroup.flags,
                    selectedVendorIds: selectedGroup.selectedVendorIds || [],
                    selectedProductIds: selectedGroup.selectedProductIds || [],
                    selectedProductTypeIds: selectedGroup.selectedProductTypeIds || [],
                },
                { headers: authHeaders() }
            );
        } catch (e) {
            console.error(e);
            alert("Failed to save. See console for details.");
        } finally {
            setSaving(false);
        }
    };

    const createGroup = async () => {
        const name = prompt("Name for the alert:");
        if (!name || !name.trim()) return;
        try {
            const { data } = await axios.post(
                withMode(`/preferences-alerts/groups`),
                { name: name.trim() },
                { headers: authHeaders() }
            );
            setGroups((prev) => [{ ...data }, ...prev]);
            setSelectedGroupId(data.id);
            setSelectedListId(data.id);
            setModalOpen(true);
        } catch (e) {
            console.error(e);
            alert("Failed to create alert.");
        }
    };

    const renameGroupForId = async (id) => {
        const current = groups.find((g) => g.id === id);
        if (!current) return;
        const name = prompt("New name:", current.name || "");
        if (!name || !name.trim()) return;
        try {
            const { data } = await axios.put(
                withMode(`/preferences-alerts/groups/${id}`),
                { name: name.trim() },
                { headers: authHeaders() }
            );
            setGroups((prev) => prev.map((g) => (g.id === id ? { ...g, ...data } : g)));
        } catch (e) {
            console.error(e);
            alert("Failed to rename alert.");
        }
    };

    const deleteGroupForId = async (id) => {
        const current = groups.find((g) => g.id === id);
        if (!current) return;
        if (!window.confirm(`Delete alert "${current.name}"?`)) return;
        try {
            await axios.delete(withMode(`/preferences-alerts/groups/${id}`), {
                headers: authHeaders(),
            });
            const remaining = groups.filter((g) => g.id !== id);
            setGroups(remaining);
            if (selectedGroupId === id) {
                setModalOpen(false);
                setSelectedGroupId(null);
            }
            setSelectedListId(remaining.length ? remaining[0].id : null);
        } catch (e) {
            console.error(e);
            alert("Failed to delete alert.");
        }
    };

    const renameCurrent = () => {
        if (selectedGroupId) renameGroupForId(selectedGroupId);
    };
    const deleteCurrent = () => {
        if (selectedGroupId) deleteGroupForId(selectedGroupId);
    };

    const filteredVendors = useMemo(() => {
        const q = vendorQ.trim().toLowerCase();
        return q ? vendors.filter((v) => v.name.toLowerCase().includes(q)) : vendors;
    }, [vendors, vendorQ]);

    const filteredProducts = useMemo(() => {
        const q = productQ.trim().toLowerCase();
        return q ? products.filter((p) => p.displayName.toLowerCase().includes(q)) : products;
    }, [products, productQ]);

    const filteredPTypes = useMemo(() => {
        const q = ptypeQ.trim().toLowerCase();
        return q
            ? productTypes.filter((t) => t.name.toLowerCase().includes(q))
            : productTypes;
    }, [productTypes, ptypeQ]);

    const selectedVendorNames = useMemo(() => {
        const ids = selectedGroup?.selectedVendorIds || [];
        return ids.map((id) => vendorById.get(id)).filter(Boolean);
    }, [selectedGroup, vendorById]);

    const selectedProductNames = useMemo(() => {
        const ids = selectedGroup?.selectedProductIds || [];
        return ids.map((id) => productById.get(id)).filter(Boolean);
    }, [selectedGroup, productById]);

    const selectedTypeNames = useMemo(() => {
        const ids = selectedGroup?.selectedProductTypeIds || [];
        return ids.map((id) => ptypeById.get(id)).filter(Boolean);
    }, [selectedGroup, ptypeById]);

    if (loading) return <Card>Loading preferences…</Card>;

    const BTN =
        "inline-flex items-center justify-center h-9 px-4 rounded-lg border text-sm font-medium " +
        "focus:outline-none focus:ring-2 disabled:opacity-50";


    return (
        <Card>
            <div
                className="flex items-center gap-2 mb-4"
                style={{
                    "--module-accent": accent,
                    "--threedi-blue": "#25408f",
                }}
            >
                <div className="font-semibold text-lg">My Alerts</div>
                <div className="ml-auto flex gap-2">
                    <button
                        className="px-3 py-1 rounded border text-white bg-[var(--module-accent)] border-[var(--module-accent)] hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[var(--module-accent)]"
                        onClick={createGroup}
                    >
                        Create Alert
                    </button>
                </div>
            </div>

            <div className="flex gap-4">
                <div className="w-full border rounded-lg">
                    <div className="p-2 font-semibold border-b">Alert Groups</div>
                    <ul className="max-h-96 overflow-auto">
                        {groups.map((g) => (
                            <li key={g.id} className="px-3 py-2 border-b last:border-b-0 hover:bg-gray-50">
                                    <button
                                        type="button"
                                        className="text-left font-medium hover:underline flex-1"
                                        onClick={() => {
                                            setSelectedListId(g.id);
                                            openGroupModal(g.id);
                                        }}
                                        title="Open"
                                    >
                                        {g.name}
                                    </button>
                            </li>
                        ))}
                        {!groups.length && (
                            <li className="px-3 py-2 text-gray-500">No alerts yet.</li>
                        )}
                    </ul>
                </div>
            </div>

            <Modal
                open={modalOpen && !!selectedGroup}
                onClose={() => setModalOpen(false)}
                title={selectedGroup?.name || "Alert"}
                accent={accent}
                actions={
                    <>

                    </>
                }
                footer={
                    <div className="flex items-center gap-2 justify-end flex-wrap">
                        <button
                            className={`${BTN} text-white bg-[var(--threedi-blue)] border-[var(--threedi-blue)] hover:opacity-90 focus:ring-[var(--threedi-blue)]`}
                            disabled={!selectedGroupId}
                            onClick={renameCurrent}
                            title="Rename this alert"
                        >
                            Rename
                        </button>

                        <button
                            className={`${BTN} text-red-600 border-red-600 hover:bg-red-50 focus:ring-red-400`}
                            disabled={!selectedGroupId}
                            onClick={deleteCurrent}
                            title="Delete this alert"
                        >
                            Delete
                        </button>

                        <button
                            className={`${BTN} text-white bg-[var(--module-accent)] border-[var(--module-accent)] hover:opacity-90 focus:ring-[var(--module-accent)]`}
                            disabled={saving}
                            onClick={saveCurrent}
                        >
                            {saving ? "Saving…" : "Save"}
                        </button>
                    </div>

                }
            >
                <div className="mb-4 flex items-center gap-2">
                    <Pill active={modalTab === "overview"} onClick={() => setModalTab("overview")} accent={accent}>
                        Overview
                    </Pill>
                    <Pill active={modalTab === "profiler"} onClick={() => setModalTab("profiler")} accent={accent}>
                        {moduleLabel}
                    </Pill>
                </div>

                {!selectedGroup ? null : modalTab === "overview" ? (
                    <div className="space-y-4">
                        {/* Flags (full width) */}
                        <div className="border rounded-lg p-3">
                            <div className="font-medium mb-2">Flags</div>
                            <label className="block">
                                <input
                                    type="checkbox"
                                    checked={!!selectedGroup.flags?.disableEmail}
                                    onChange={() => toggleFlag("disableEmail")}
                                />{" "}
                                Disable email notifications (still appears in My Profiler)
                            </label>
                            <label className="block">
                                <input
                                    type="checkbox"
                                    checked={!!selectedGroup.flags?.newVendor}
                                    onChange={() => toggleFlag("newVendor")}
                                />{" "}
                                Alert me when new {itemLabel ? `${itemLabel} ` : ""}Vendors are added
                            </label>
                            <label className="block">
                                <input
                                    type="checkbox"
                                    checked={!!selectedGroup.flags?.newProduct}
                                    onChange={() => toggleFlag("newProduct")}
                                />{" "}
                                Alert me when new {itemLabel ? `${itemLabel} ` : ""}Products are added
                            </label>
                        </div>

                        {/* Three cards side-by-side */}
                        <div className="grid md:grid-cols-3 gap-4">
                            {/* Selected Vendors */}
                            <div className="border rounded-lg p-3">
                                <div className="font-medium mb-2">Selected Vendors</div>
                                <div className="text-sm text-gray-600">
                                    {selectedGroup.selectedVendorIds?.length || 0} selected
                                </div>
                                <div className="mt-2 max-h-40 overflow-auto">
                                    {(selectedVendorNames || []).map((name, idx) => (
                                        <div key={idx} className="text-sm py-0.5">• {name}</div>
                                    ))}
                                </div>
                            </div>

                            {/* Selected Products */}
                            <div className="border rounded-lg p-3">
                                <div className="font-medium mb-2">Selected Products</div>
                                <div className="text-sm text-gray-600">
                                    {selectedGroup.selectedProductIds?.length || 0} selected
                                </div>
                                <div className="mt-2 max-h-40 overflow-auto">
                                    {(selectedProductNames || []).map((name, idx) => (
                                        <div key={idx} className="text-sm py-0.5">• {name}</div>
                                    ))}
                                </div>
                            </div>

                            {/* Selected Product Types */}
                            <div className="border rounded-lg p-3">
                                <div className="font-medium mb-2">Selected Product Types</div>
                                <div className="text-sm text-gray-600">
                                    {selectedGroup.selectedProductTypeIds?.length || 0} selected
                                </div>
                                <div className="mt-2 flex flex-wrap gap-2 max-h-40 overflow-auto">
                                    {(selectedTypeNames || []).map((name, idx) => (
                                        <span key={idx} className="text-xs px-2 py-1 rounded-full border bg-gray-50">
                      {name}
                    </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-4">
                        <div className="grid md:grid-cols-3 gap-4">
                            <div className="border rounded-lg p-3">
                                <div className="font-medium mb-2">Vendors</div>
                                <Search value={vendorQ} onChange={setVendorQ} placeholder="Filter vendors" className="mb-2" />
                                <div className="flex gap-2 mb-2">
                                    <button
                                        className="px-2 py-1 border rounded"
                                        onClick={() =>
                                            setAllNone("selectedVendorIds", filteredVendors.map((v) => v.id), true)
                                        }
                                    >
                                        All
                                    </button>
                                    <button
                                        className="px-2 py-1 border rounded"
                                        onClick={() => setAllNone("selectedVendorIds", [], false)}
                                    >
                                        None
                                    </button>
                                </div>
                                <div className="max-h-64 overflow-auto text-sm">
                                    {filteredVendors.map((v) => {
                                        const checked = new Set(selectedGroup.selectedVendorIds || []).has(v.id);
                                        return (
                                            <label key={v.id} className="flex gap-2 py-1">
                                                <input
                                                    type="checkbox"
                                                    checked={checked}
                                                    onChange={(e) => toggleId("selectedVendorIds", v.id, e.target.checked)}
                                                />
                                                <span>{v.name}</span>
                                            </label>
                                        );
                                    })}
                                </div>
                            </div>

                            <div className="border rounded-lg p-3">
                                <div className="font-medium mb-2">Products</div>
                                <Search value={productQ} onChange={setProductQ} placeholder="Filter products" className="mb-2" />
                                <div className="flex gap-2 mb-2">
                                    <button
                                        className="px-2 py-1 border rounded"
                                        onClick={() =>
                                            setAllNone("selectedProductIds", filteredProducts.map((p) => p.id), true)
                                        }
                                    >
                                        All
                                    </button>
                                    <button
                                        className="px-2 py-1 border rounded"
                                        onClick={() => setAllNone("selectedProductIds", [], false)}
                                    >
                                        None
                                    </button>
                                </div>
                                <div className="max-h-64 overflow-auto text-sm">
                                    {filteredProducts.map((p) => {
                                        const checked = new Set(selectedGroup.selectedProductIds || []).has(p.id);
                                        return (
                                            <label key={p.id} className="flex gap-2 py-1">
                                                <input
                                                    type="checkbox"
                                                    checked={checked}
                                                    onChange={(e) => toggleId("selectedProductIds", p.id, e.target.checked)}
                                                />
                                                <span>{p.displayName}</span>
                                            </label>
                                        );
                                    })}
                                </div>
                            </div>

                            <div className="border rounded-lg p-3">
                                <div className="font-medium mb-2">Product Type</div>
                                <Search value={ptypeQ} onChange={setPtypeQ} placeholder="Filter product types" className="mb-2" />
                                <div className="flex gap-2 mb-2">
                                    <button
                                        className="px-2 py-1 border rounded"
                                        onClick={() =>
                                            setAllNone(
                                                "selectedProductTypeIds",
                                                filteredPTypes.map((t) => t.id),
                                                true
                                            )
                                        }
                                    >
                                        All
                                    </button>
                                    <button
                                        className="px-2 py-1 border rounded"
                                        onClick={() => setAllNone("selectedProductTypeIds", [], false)}
                                    >
                                        None
                                    </button>
                                </div>
                                <div className="max-h-64 overflow-auto text-sm">
                                    {filteredPTypes.map((t) => {
                                        const checked = new Set(selectedGroup.selectedProductTypeIds || []).has(t.id);
                                        return (
                                            <label key={t.id} className="flex gap-2 py-1">
                                                <input
                                                    type="checkbox"
                                                    checked={checked}
                                                    onChange={(e) => toggleId("selectedProductTypeIds", t.id, e.target.checked)}
                                                />
                                                <span>{t.name}</span>
                                            </label>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </Modal>
        </Card>
    );
}
